import React, { useCallback, useEffect } from "react";
import SidePanel from "../../../../../../shared-components/css-modules/lexus/organisms/SidePanel";
import type { ModalViewType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import type { OtherGradeModalType } from "../../../../../utils/modalConstants";
import Container from "../../../../../../../../common-deprecated/styles/lexus/Container";
import styles from "./OtherGradeModal.module.scss";
import ColorOtherGradesModal from "../../../configure/sections/colours/ColorOtherGradesModal";
import { useCarConfigCCISNormalization } from "../../../../../hooks/useAphCCISNormalization";
import { type CarConfigDispatchType, useCarConfigSelector } from "../../../../../redux/store";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import EngineOtherGradesModal from "../../../configure/sections/engine/EngineOtherGradesModal";
import WheelOtherGradesModal from "../../../configure/sections/wheels/WheelOtherGradesModal";
import ExteriorSlider from "../../spin/exterior-slider/ExteriorSlider";
import { startAvailableGradesForConfigurableFetch } from "../../../../../redux/slices/CarConfigAvailableInOtherGradesSlice";
import UpholsteryOtherGradesModal from "../../../configure/sections/upholstery/UpholsteryOtherGradesModal";
import {
    trackAvailableInOtherGradesBackToCurrentGrade,
    trackAvailableInOtherGradesGoToNewGrade,
} from "../../../../../utils/tracking";
import useAphAvailableInOtherGrades from "../../../../../hooks/useAphAvailableInOtherGrades";
import { find } from "../../../../../../../../common-deprecated/utils";
import UpholsterySpin from "../../spin/Upholstery";
import { AvailableInOtherGradeModalType } from "../../../../../utils/types/SectionTypes";
import { getVisual, overrideCCISImageConfig } from "../../../../../utils/availableInOtherGradesUtils";
import { Loader } from "../../../../../../shared-components/css-modules/lexus/atoms/Loader";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import { useDispatch } from "../../../../../../../../shared-logic/redux/store";

const OtherGradeModal = (props: ModalViewType<OtherGradeModalType>): JSX.Element => {
    const { show, close, modalSettings } = props;
    const { availableInOtherGradesProps } = modalSettings;
    const { section, motorization, upholstery, wheel, colour } = availableInOtherGradesProps;

    const dispatch = useDispatch<CarConfigDispatchType>();

    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const grade = useCarConfigSelector((state) => state.car.grade);
    const model = useCarConfigSelector((state) => state.car.model);
    const wheelsInOtherGrades = useCarConfigSelector((state) => state.availableInOtherGrades.availableWheels);

    const { fetching, availableGradesForConfigurable, selectedGrade, url } = useAphAvailableInOtherGrades();
    const carImageInput = useCarConfigCCISNormalization();
    const modifiedCarImageInput = overrideCCISImageConfig(carImageInput, selectedGrade);

    let configurationObjectType: "car" | "colour" | "upholstery" | "wheel" | undefined;
    let configurationObjectId: string | undefined;
    switch (section) {
        case AvailableInOtherGradeModalType.Engine:
            configurationObjectType = "car";
            configurationObjectId = motorization?.carId;
            break;
        case AvailableInOtherGradeModalType.Colour:
            configurationObjectType = "colour";
            configurationObjectId = colour?.id;
            break;
        case AvailableInOtherGradeModalType.Wheel:
            configurationObjectType = "wheel";
            configurationObjectId = wheel?.id;
            break;
        case AvailableInOtherGradeModalType.Upholstery:
            configurationObjectType = "upholstery";
            configurationObjectId = upholstery?.id;
            break;
        default:
            break;
    }

    const onSelect = useCallback(
        (variant: "open" | "close") => {
            if (selectedGrade && section) {
                if (variant === "close") {
                    trackAvailableInOtherGradesBackToCurrentGrade(grade.name, section);
                    close();
                } else if (variant === "open" && url) {
                    trackAvailableInOtherGradesGoToNewGrade(selectedGrade.name, section);
                    window.location.href = url;
                }
            }
        },
        [close, section, selectedGrade, url],
    );

    const otherGradeVisualData = getVisual(
        section === AvailableInOtherGradeModalType.Engine ? 3 : 12,
        selectedGrade,
        model.id,
        commonSettings,
        modifiedCarImageInput,
    );

    useEffect(() => {
        if (configurationObjectId && configurationObjectType)
            dispatch(
                startAvailableGradesForConfigurableFetch({
                    configurationObjectType,
                    configurationObjectId,
                }),
            );
    }, [dispatch, configurationObjectId, configurationObjectType]);

    return (
        <SidePanel
            open={show}
            variant="fullscreen"
            fromDirection="block-end"
            onRequestClose={close}
            className={styles.modal}
            topLayer={false}
        >
            <Container className={styles.container}>
                <div className={styles.grid}>
                    {fetching && (
                        <div className={styles.loader}>
                            <Loader />
                        </div>
                    )}
                    {!fetching && availableGradesForConfigurable && selectedGrade && (
                        <>
                            <div>
                                <div>
                                    {section === AvailableInOtherGradeModalType.Engine && motorization ? (
                                        <EngineOtherGradesModal motorization={motorization} />
                                    ) : section === AvailableInOtherGradeModalType.Colour && colour ? (
                                        <ColorOtherGradesModal colour={colour} />
                                    ) : section === AvailableInOtherGradeModalType.Wheel && wheel ? (
                                        <WheelOtherGradesModal wheel={wheel} />
                                    ) : (
                                        section === AvailableInOtherGradeModalType.Upholstery &&
                                        upholstery && <UpholsteryOtherGradesModal upholstery={upholstery} />
                                    )}
                                </div>
                                <div className={styles.actions}>
                                    <Button onClick={() => onSelect("close")} variant="btn-default">
                                        <CarConfigLabel
                                            label="carConfigAvailableInOtherGradeCloseModal"
                                            params={{
                                                model: model.webtitleSectionName,
                                                currentGrade: grade.webtitleSectionName,
                                            }}
                                        />
                                    </Button>
                                    <Button href={url} onClick={() => onSelect("open")} variant="btn-secondary">
                                        <CarConfigLabel label="carConfigAvailableInOtherGradeSwitchGrade" />
                                    </Button>
                                </div>
                            </div>
                            <div
                                className={
                                    section === AvailableInOtherGradeModalType.Upholstery
                                        ? styles["upholstery-column"]
                                        : ""
                                }
                            >
                                {section === AvailableInOtherGradeModalType.Wheel ? (
                                    <div className={styles.slider}>
                                        <ExteriorSlider
                                            frames={[3]}
                                            imageOptions={{
                                                bgColour: "F4F6FB",
                                                width: 1000,
                                                height: 450,
                                                padding: 0,
                                                useAvailableInOtherGradeImages: otherGradeVisualData && {
                                                    overrideCarImageInput: modifiedCarImageInput,
                                                    overrideCarIds: otherGradeVisualData.carIds,
                                                    wheelImage:
                                                        find(selectedGrade.wheel.id, wheelsInOtherGrades)?.image ?? "",
                                                },
                                            }}
                                            isWheel
                                        />
                                    </div>
                                ) : section === AvailableInOtherGradeModalType.Upholstery && show ? (
                                    <div className={styles["upholstery-image"]}>
                                        <UpholsterySpin
                                            overrideCarConfig={
                                                otherGradeVisualData && {
                                                    imageCarConfig: modifiedCarImageInput,
                                                    carIds: otherGradeVisualData.carIds,
                                                }
                                            }
                                        />
                                    </div>
                                ) : (
                                    <img
                                        src={otherGradeVisualData?.url}
                                        alt=""
                                        role="presentation"
                                        className={styles.image}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Container>
        </SidePanel>
    );
};

export default OtherGradeModal;
